import op from 'object-path';
import store from '@/store';
import $api from '@/modules/request';
import Storage from '@/modules/storage';

const mtm = {
  autoRefreshOn401Added: false,
  setToken(token, expiredIn) {
    store.commit('mtmUser/SET_TOKEN', token);
    if (expiredIn) {
      store.commit('mtmUser/SET_TOKEN_EXPIRY', Date.now() + expiredIn * 1000);
    }
    $api.mtm.defaults.headers['X-Mtm-User-Access-Token'] = token;
    if (!token) {
      Storage.removeMtmUserToken();
    }
  },
  setAuth(auth) {
    store.commit('mtmUser/SET_AUTH', auth);
    if (!auth) {
      Storage.removeMtmUserAuth();
      return;
    }
    Storage.setMtmUserAuth(auth);
    if (typeof this.autoRefreshOn401Added === 'number') {
      $api.mtm.interceptors.response.eject(this.autoRefreshOn401Added);
    }

    this.autoRefreshOn401Added = $api.mtm.interceptors.response.use(
      response => response,
      error => {
        const originalRequest = error.config;
        const { status } = error.response || {};
        const requestUrl = originalRequest.url;

        if (status !== 401 || requestUrl.endsWith('auth') || requestUrl.endsWith('signin')) {
          return Promise.reject(error);
        }

        if (originalRequest.isRetrying) return Promise.reject(error);

        console.log('[autoRefreshOn401] start');
        originalRequest.isRetrying = true;
        const authData = Storage.getMtmUserAuth();
        return this.auth(authData).then(() => {
          console.log('[autoRefreshOn401] successfully refreshed token');
          // originalRequest.headers['X-Mtm-User-Access-Token'] = token;
          return $api.mtm(originalRequest); // TODO: would the token be updated here?
        });
      }
    );
  },
  async confirmLogin(data) {
    await $api.user.post('/api/v1/mtm_users/signin', data);
  },
  async login(data) {
    const { data: auth } = await $api.user.post('/api/v1/mtm_users/signin', data);
    this.setAuth(auth);
    await this.auth(auth);
  },
  logout() {
    store.dispatch('mtmUser/reset');
    store.dispatch('app/setAppMode', '');
  },
  async auth(authData) {
    const { data } = await $api.user.post('/api/v1/mtm_users/auth', authData);
    const { access_token: token, expired_in: expiredIn } = data;
    this.setToken(token, expiredIn);
    Storage.setMtmUserToken({ token, expiredIn });
  },
  async registerMember(data) {
    const resp = await $api.mtm.post('/api/v1/mtm_users/users/register_member', data);
    return resp.data;
  },
  async getMe() {
    const resp = await $api.mtm.get('/api/v1/mtm_users/me');
    store.commit('mtmUser/SET_ME', { ...resp.data });
  },
  async getStoreConfigs(storeId) {
    console.log('getting store configs', storeId);
    store.commit('user/SET_STORE_ID', storeId);
    const params = { sub_config: storeId };
    const resp = await $api.mtm.get('/api/v1/remote_config', { params });
    console.log(resp.data);
    return JSON.parse(op.get(resp, 'data.configs.Store', '{}'));
  },
  async getRemoteConfig() {
    const resp = await $api.mtm.get('/api/v1/remote_config');
    const configs = JSON.parse(op.get(resp, 'data.configs.AppSettings', '{}'));
    const { mtm_users: users = {}, companies = {}, default: defaultConfig = {} } = configs;
    let myCompanyConfig = {};
    let myUserConfig = {};
    const companyId = store.getters['mtmUser/companyId'];
    const myAccount = store.getters['mtmUser/myAccount'];
    if (companyId) {
      myCompanyConfig = companies[companyId] || {};
    }
    if (myAccount) {
      myUserConfig = users[myAccount] || {};
    }
    const mergedConfig = {
      ...defaultConfig,
      ...myCompanyConfig,
      ...myUserConfig,
    };
    store.dispatch('mtmUser/setConfig', mergedConfig);
    store.dispatch('app/setAppMode', mergedConfig.mode || '');
  },
  async getScannersConfigs() {
    const resp = await $api.mtm.get('/api/v1/remote_config');
    const configs = JSON.parse(op.get(resp, 'data.configs.AppSettings', '{}'));
    const {scanners = {} } = configs;
    Object.keys(scanners).forEach(key => {
      const { ref } = scanners[key];
      if (!ref) return;
      if (!scanners[ref]) return;

      scanners[key] = scanners[ref];
    })
    store.dispatch('app/setScannersConfigs', scanners);
  },
};

const user = {
  autoRefreshOn401Added: false,
  async setProfile(profile) {
    await $api.user.post('/api/v1/users/profile', profile);
    return this.getProfile();
  },
  async setAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);
    await $api.user.post('/api/v1/users/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  async getProfile() {
    const { data: profile } = await $api.user.get('/api/v1/users/profile');
    if (profile.gender >= 0) {
      store.commit('user/SET_ME', profile);
    }
    return profile;
  },
  async setUserCredit() {
    await $api.user.get('/api/v1/mobile_scans/credits');
    return this.getUserCredit();
  },
  async getUserCredit() {
    const { data: credits } = await $api.user.get('/api/v1/mobile_scans/credits');
    if (credits) {
      store.commit('user/SET_CREDITS', credits.credits);
    }
    return credits;
  },
  async auth(authData) {
    const { data } = await $api.user.post('/api/v1/users/auth', authData);
    const { access_token: token, expired_in: expiredIn } = data;
    this.setToken(token, expiredIn);
    Storage.setUserToken({ token, expiredIn });
    this.tempAuth(authData);
  },
  async tempAuth(authData) {
    const { data } = await $api.user.post('/api/v1/users/auth', { ...authData, long_live: true });
    const { access_token: token, expired_in: expiredIn } = data;
    this.setTempToken(token, expiredIn);
    this.getUserCredit();
  },
  setToken(token, expiredIn) {
    store.commit('user/SET_TOKEN', token);
    if (expiredIn) {
      store.commit('user/SET_TOKEN_EXPIRY', Date.now() + expiredIn * 1000);
    }
    $api.user.defaults.headers['X-User-Access-Token'] = token;

    if (!token) {
      Storage.removeUserToken();
    }
  },
  setTempToken(token, expiredIn) {
    store.commit('user/SET_LONG_TOKEN', token);
    if (expiredIn) {
      store.commit('user/SET_LONG_TOKEN_EXPIRY', Date.now() + expiredIn * 1000);
    }
  },
  setAuth(auth) {
    store.commit('user/SET_AUTH', auth);
    if (!auth) {
      Storage.removeUserAuth();
      return;
    }
    Storage.setUserAuth(auth);
    if (typeof this.autoRefreshOn401Added === 'number') {
      $api.user.interceptors.response.eject(this.autoRefreshOn401Added);
    }

    this.autoRefreshOn401Added = $api.user.interceptors.response.use(
      response => response,
      error => {
        const originalRequest = error.config;
        const { status } = error.response || {};
        const requestUrl = originalRequest.url;

        if (originalRequest.isRetrying) return Promise.reject(error);

        if (status === 408 && requestUrl.indexOf('scanners') > -1) {
          originalRequest.isRetrying = true;
          return $api.user(originalRequest);
        }

        if (status !== 401 || requestUrl.endsWith('auth') || requestUrl.endsWith('signin')) {
          return Promise.reject(error);
        }

        console.log('[userAutoRefreshOn401] start');
        originalRequest.isRetrying = true;
        const authData = Storage.getUserAuth();
        return this.auth(authData).then(() => {
          console.log('[userAutoRefreshOn401] successfully refreshed token');
          // remove the old token to ensure that the default header can overwrite it.
          delete originalRequest.headers['X-User-Access-Token'];
          return $api.user(originalRequest);
        });
      }
    );
  },
  async logout() {
    await store.dispatch('user/reset');
  },
  async login(data) {
    const { data: auth } = await $api.user.post('/api/v1/users/signin', data);
    this.setAuth(auth);
    await this.auth(auth);
  },
  async loginSNS(data) {
    const { data: auth } = await $api.user.post('/api/v1/users/signin_sns', data);
    this.setAuth(auth);
    await this.auth(auth);
  },
  async registerEmail(data) {
    const resp = await $api.user.post('/api/v1/users/register_email', data);
    return (resp.data && resp.data.username) || '';
  },
  async registerPhone(data) {
    const resp = await $api.user.post('/api/v1/users/register_phone', data);
    return (resp.data && resp.data.username) || '';
  },
  async checkPhoneAvailable(data) {
    const resp = await $api.user.post('/api/v1/users/check_account', data);
    return resp.data && resp.data.available;
  },
  async requestConfirmationToken(data) {
    await $api.user.post('/api/v1/users/req_confirmation_token', data);
  },
  async confirmToken(data) {
    await $api.user.post('/api/v1/users/confirm_phone', data);
  },
  async requestResetPassword(data) {
    await $api.user.post('/api/v1/users/req_reset_pwd', data);
  },
  async confirmPhonePassword(data) {
    await $api.user.post('/api/v1/users/confirm_phone_pwd', data);
  },
  async remoteSignIn(token) {
    await $api.user.post('/api/v1/users/remote_signin/signin', { signin_token: token });
  },
  async deleteAccount() {
    await $api.user.delete('/api/v1/users/me');
  },
  async purchaseCredits(payload) {
    const response = await $api.user.post('/api/v1/mobile_scans/credits', payload);
    return response;
  },
  async getSizeXtName() {
    const { data } = await $api.user.get(`/api/v1/scan_records/size_xt_name`);
    return data;
  },
};

const ApiActions = {
  mtm,
  user,
  isAuthenticated() {
    const isAuthenticated =
      store.getters['user/accessToken'] || store.getters['mtmUser/isAuthenticated'];
    if (!isAuthenticated) {
      console.log('not authenticated');
    }
    return isAuthenticated;
  },
};

export default ApiActions;
